import { useEffect, useRef } from "react";
import { LoadingDots } from "./LoadingDots";
import { useAudioLevel } from "../hooks/useAudioLevel";
import { strings } from "../strings";

/**
 * Simple audio-level-based voice visualizer bars
 * Matches @workspace/ui VoiceVisualizerBars implementation
 */
function VoiceVisualizerBars({
  audioTrack = null,
}: {
  audioTrack?: MediaStreamTrack | null;
}) {
  const { audioLevel } = useAudioLevel(audioTrack);

  // Create 5 bars with varying heights based on audio level
  const bars = [0.6, 1.0, 0.8, 1.0, 0.6]; // Base multipliers for visual variation

  return (
    <div className="flex items-center gap-0.5 h-6">
      {bars.map((multiplier, i) => {
        // Calculate bar height based on audio level
        const baseHeight = 4;
        const maxHeight = 24;
        const height =
          baseHeight + audioLevel * (maxHeight - baseHeight) * multiplier;

        return (
          <div
            key={i}
            className="w-[3px] bg-muted-foreground rounded-sm transition-[height] duration-75 ease-out"
            style={{ height: `${height}px` }}
          />
        );
      })}
    </div>
  );
}

export type ConversationStatus = "idle" | "listening" | "thinking";

interface ConversationStatusIndicatorProps {
  status: ConversationStatus;
  /** Audio track for visualizer when listening (optional - will animate without) */
  audioTrack?: MediaStreamTrack | null;
  className?: string;
}

/**
 * Conversation status indicator - matches web app styling exactly
 * Shows "Listening" with voice visualizer or "Thinking" with loading dots
 */
export function ConversationStatusIndicator({
  status,
  audioTrack = null,
  className,
}: ConversationStatusIndicatorProps) {
  const isActive = status !== "idle";
  const lastActiveStatusRef = useRef<ConversationStatus>(status);

  useEffect(() => {
    if (status !== "idle") {
      lastActiveStatusRef.current = status;
    }
  }, [status]);

  const displayStatus = isActive ? status : lastActiveStatusRef.current;

  return (
    <div
      className={`flex items-center justify-center gap-2 ${className || ""}`}
    >
      {/* Main status indicator - pill style matching Shadcn */}
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-sm font-medium text-muted-foreground transition-all duration-300 ${
          isActive
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
      >
        {displayStatus === "listening" && (
          <>
            <VoiceVisualizerBars audioTrack={audioTrack} />
            <span>{strings.conversationStatus.listening}</span>
          </>
        )}
        {displayStatus === "thinking" && (
          <>
            <LoadingDots size="sm" />
            <span>{strings.conversationStatus.thinking}</span>
          </>
        )}
      </div>
    </div>
  );
}
